import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import React from 'react';
import {
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useChartColors } from 'src/UIKit';
import {
  areMetricTotalsEmpty,
  chartCardContentSx,
  chartCardHeaderSx,
  chartCardSx,
  CHART_TICK_FONT_SIZE,
  formatMetricValue,
  getMetricTotals,
  getYAxisWidth,
  LINE_PIE_TYPES,
} from '../../../helpers';
import ChartTypeToggle from '../../ChartTypeToggle';
import ChartEmptyState from '../ChartEmptyState';
import ChartLegend from '../ChartLegend';
import { ChartType, MetricChartData, MetricChartLine } from '../../../types';

export default function LinePieChartCard({
  title,
  lines,
  data,
  type,
  onTypeChange,
}: {
  title: string;
  lines: MetricChartLine[];
  data: MetricChartData[];
  type: ChartType;
  onTypeChange: (type: ChartType) => void;
}) {
  const { grid: chartGridStroke } = useChartColors();
  const totals = getMetricTotals(data);
  const isEmpty = areMetricTotalsEmpty(totals, lines);
  const yAxisWidth = getYAxisWidth(data, lines);
  const pieData = lines.map(line => ({
    name: line.name,
    value: totals[line.dataKey] ?? 0,
    color: line.color,
  }));

  const renderChart = () => {
    if (type === 'pie') {
      return (
        <PieChart>
          <Pie
            data={pieData}
            dataKey="value"
            nameKey="name"
            outerRadius="85%"
            label
            animationDuration={300}
          >
            {pieData.map(entry => (
              <Cell key={entry.name} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip formatter={(value: number) => formatMetricValue(value)} />
        </PieChart>
      );
    }
    return (
      <LineChart data={data} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
        <CartesianGrid vertical={false} stroke={chartGridStroke} />
        <XAxis dataKey="label" tick={{ fontSize: CHART_TICK_FONT_SIZE }} minTickGap={16} />
        <YAxis tickFormatter={formatMetricValue} tick={{ fontSize: CHART_TICK_FONT_SIZE }} width={yAxisWidth} />
        <Tooltip formatter={(value: number) => formatMetricValue(value)} />
        {lines.map(line => (
          <Line
            key={line.dataKey}
            type="monotone"
            dataKey={line.dataKey}
            name={line.name}
            stroke={line.color}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
            animationDuration={300}
          />
        ))}
      </LineChart>
    );
  };

  return (
    <Card variant="outlined" sx={chartCardSx}>
      <CardContent sx={{ ...chartCardContentSx, height: 440 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={chartCardHeaderSx}>
          <Typography fontSize="1.1rem" fontWeight="fontWeightSemiBold">{title}</Typography>
          {!isEmpty && <ChartTypeToggle value={type} onChange={onTypeChange} types={LINE_PIE_TYPES} />}
        </Stack>
        <Box flex={1} minHeight={0}>
          {isEmpty ? (
            <ChartEmptyState />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              {renderChart()}
            </ResponsiveContainer>
          )}
        </Box>
        {!isEmpty && <ChartLegend lines={lines} totals={totals} />}
      </CardContent>
    </Card>
  );
}
